import React, { useEffect, useState, useContext, useRef } from "react";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import format from "date-fns/format";
import parse from "date-fns/parse";
import startOfWeek from "date-fns/startOfWeek";
import getDay from "date-fns/getDay";
import enUS from "date-fns/locale/en-US";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateTimePicker } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  IconButton,
  InputBase,
  Avatar,
  Box,
  Typography,
  Chip,
  Autocomplete,
  InputAdornment,
  Grid,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
} from "@mui/material";
import { ArrowBackIos, ArrowForwardIos, Search } from "@mui/icons-material";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { apiRequest } from "../../../api/apiHelper";
import DailyTasks from "../DailyTasks";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import { AddEventContext } from "../HomeLayout";
import { FiPlus } from "react-icons/fi";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

// Custom styles for calendar theme
const calendarThemeStyles = `
  .rbc-toolbar button,
  .rbc-toolbar button:active {
    background: var(--primary-color) !important;
    color: #fff !important;
    border-radius: 6px;
    border: none;
    font-weight: 600;
    margin: 0 2px;
    box-shadow: none;
  }
  .rbc-toolbar button.rbc-active {
    background: #fff !important;
    color: var(--primary-color) !important;
    border-radius: 6px;
    border: 2px solid var(--primary-color) !important;
    font-weight: 700;
    margin: 0 2px;
    box-shadow: none;
  }
  .rbc-event {
    background-color: var(--primary-color) !important;
    border: none !important;
    color: #fff !important;
    border-radius: 6px !important;
    font-weight: 500;
    padding: 2px 6px;
  }
  .rbc-selected-cell,
  .rbc-selected {
    background-color: var(--primary-color) !important;
    opacity: 0.15;
  }
  .rbc-today {
    background: transparent !important;
  }
`;

const locales = {
  "en-US": enUS,
};
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay,
  locales,
});

// Custom event renderer for Google Calendar style
function CustomEvent({ event }) {
  const color = event.type === "mine" ? "#1a73e8" : "#0b8043";
  return (
    <div
      style={ {
        background: color,
        color: "#fff",
        borderRadius: 4,
        padding: "2px 8px",
        fontWeight: 500,
        fontSize: 12,
        lineHeight: "20px",
        cursor: "pointer",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      } }
      className="custom-gcal-event"
      title={ event.title }
    >
      { event.title }
    </div>
  );
}

// Custom header for week/day view
function CustomTimeHeader({ label, date }) {
  const isToday = date && format(date, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
  const dayName = date ? format(date, 'EEE').toUpperCase() : '';
  const dayNumber = date ? format(date, 'd') : '';

  return (
    <div style={ { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '8px 0' } }>
      <span style={ { fontSize: 11, fontWeight: 500, color: isToday ? '#1a73e8' : '#1a1a1a', letterSpacing: '0.5px' } }>{ dayName }</span>
      <span style={ {
        fontSize: 24,
        fontWeight: 400,
        color: isToday ? '#fff' : '#202124',
        background: isToday ? '#1a73e8' : 'transparent',
        borderRadius: '50%',
        width: 40,
        height: 40,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: 4,
      } }>
        { dayNumber }
      </span>
    </div>
  );
}

// Additional Google Calendar-like CSS
const googleCalendarOverrides = `
  .rbc-calendar {
    border: none;
    font-family: 'Google Sans', Roboto, Arial, sans-serif;
    height: 100% !important;
  }
  .rbc-month-view, .rbc-time-view {
    border: 1px solid #dadce0;
    border-radius: 8px;
    background: #fff;
    height: 100% !important;
  }
  .rbc-month-view {
    display: flex;
    flex-direction: column;
    flex: 1;
  }
  .rbc-month-header {
    flex: 0 0 auto;
  }
  .rbc-month-row {
    flex: 1 1 0;
    min-height: 0;
    overflow: visible;
  }
  .rbc-today {
    background: transparent !important;
  }
  .rbc-day-bg.rbc-today {
    background: transparent !important;
  }
  .rbc-header {
    font-weight: 500;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    background: transparent;
    border-bottom: 1px solid #dadce0;
    color: #1a1a1a !important;
    padding: 12px 0;
    text-align: center;
  }
  .rbc-time-header-content {
    border-left: none;
    display: flex;
    flex: 1;
  }
  .rbc-time-header-content > .rbc-row.rbc-row-resource {
    border-bottom: 1px solid #dadce0;
  }
  .rbc-day-bg {
    border-left: 1px solid #dadce0;
  }
  .rbc-day-bg:first-child {
    border-left: none;
  }
  .rbc-time-header-content .rbc-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 8px 4px;
    border-bottom: 1px solid #dadce0;
    border-left: 1px solid #dadce0;
    border-right: none;
    min-height: auto;
    flex: 1 1 0 !important;
    min-width: 0 !important;
    width: 0 !important;
  }
  .rbc-time-header-content .rbc-header:first-child {
    border-left: none;
  }
  .rbc-time-view.rbc-time-view-day .rbc-time-header-content .rbc-header {
    align-items: flex-start;
    padding-left: 16px;
  }
  .rbc-time-header-content .rbc-header:last-child {
    border-right: 1px solid #dadce0;
  }
  .rbc-time-header-content .rbc-header.rbc-today {
    background: transparent !important;
  }
  .rbc-allday-cell {
    display: none;
  }
  .rbc-time-header-content .rbc-row.rbc-time-header-cell {
    min-height: auto;
  }
  .rbc-time-header-content .rbc-row.rbc-time-header-cell + .rbc-row {
    display: none;
  }
  .rbc-row-segment {
    padding: 1px 4px;
  }
  .rbc-row-segment .custom-gcal-event {
    margin: 1px 0;
  }
  .custom-gcal-event:hover {
    filter: brightness(0.95);
  }
  .rbc-event {
    background: none !important;
    color: inherit !important;
    border: none !important;
    box-shadow: none !important;
    padding: 0 !important;
    border-radius: 4px !important;
  }
  .rbc-event-content {
    overflow: hidden;
  }
  .rbc-date-cell {
    padding: 4px 0;
    text-align: center;
  }
  .rbc-date-cell > a,
  .rbc-date-cell > span,
  .rbc-date-cell > button {
    font-size: 14px;
    font-weight: 500;
    color: #202124;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    text-decoration: none;
  }
  .rbc-date-cell.rbc-now > a,
  .rbc-date-cell.rbc-now > span,
  .rbc-date-cell.rbc-now > button {
    background: #1a73e8;
    color: #fff !important;
  }
  .rbc-button-link {
    font-size: 14px;
    color: #202124;
    font-weight: 500;
  }
  .rbc-off-range-bg {
    background: #f8f9fa;
  }
  .rbc-off-range .rbc-button-link {
    color: #70757a;
  }
  .rbc-time-content {
    border-top: 1px solid #dadce0;
    overflow-y: auto;
  }
  .rbc-time-gutter {
    background: #fff !important;
    border-right: none !important;
    width: 60px !important;
    min-width: 60px !important;
    flex: 0 0 60px !important;
  }
  .rbc-time-gutter .rbc-timeslot-group {
    min-height: 48px;
    height: 48px;
    position: relative;
    border-bottom: 1px solid transparent !important;
    box-sizing: border-box !important;
  }
  .rbc-time-gutter .rbc-time-slot {
    border: none !important;
  }
  .rbc-time-content .rbc-time-gutter {
    font-size: 10px;
    font-weight: 400;
    color: #1a1a1a;
    text-align: right;
    padding-right: 12px;
  }
  .rbc-time-header-gutter {
    width: 60px !important;
    min-width: 60px !important;
    flex: 0 0 60px !important;
    border-bottom: none !important;
   border-right: 1px solid #dadce0 !important;
    background: #fff !important;
    position: relative;
  }
  .rbc-time-header {
    border-bottom: 1px solid #dadce0 !important;
  }
  .rbc-time-slot {
    min-height: 48px;
    border-top: none;
  }
  .rbc-timeslot-group {
    border-bottom: 1px solid #dadce0;
    min-height: 48px;
    height: 48px;
    box-sizing: border-box !important;
  }
  .rbc-day-slot .rbc-time-slot {
    border-top: none;
  }
  .rbc-day-slot .rbc-timeslot-group {
    border-bottom: 1px solid #dadce0;
  }
  .rbc-current-time-indicator {
    background-color: #ea4335 !important;
    height: 2px !important;
    z-index: 3;
  }
  .rbc-current-time-indicator::before {
    content: '';
    position: absolute;
    left: -6px;
    top: -5px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background-color: #ea4335;
  }
  .rbc-time-column {
    min-height: 100%;
    border-left: 1px solid #dadce0;
    border-right: none;
    flex: 1 1 0;
    min-width: 0;
  }
  .rbc-time-column:first-child {
    border-left: none;
  }
  .rbc-time-column:last-child {
    border-right: 1px solid #dadce0;
  }
  .rbc-time-content > * + * > * {
    border-left: none;
  }
  .rbc-day-slot .rbc-event {
    border: none !important;
    border-radius: 4px !important;
    background: #1a73e8 !important;
    padding: 4px 6px !important;
    min-height: 3px !important;
  }
  .rbc-day-slot .rbc-event .rbc-event-label {
    display: none;
  }
  .rbc-day-slot .rbc-event .rbc-event-content {
    font-size: 11px;
    font-weight: 500;
    color: #fff;
    white-space: normal;
    overflow: visible;
    line-height: 1.2;
  }
  .rbc-day-slot .rbc-events-container {
    margin-right: 0;
  }
  .rbc-time-view .rbc-row {
    min-height: auto;
  }
  .rbc-time-header.rbc-overflowing {
    border-right: 1px solid #dadce0;
  }
  .rbc-time-header-gutter {
    border-bottom: 1px solid #dadce0;
    border-right: 1px solid #dadce0;
  }
  .rbc-label {
    font-size: 10px;
    font-weight: 400;
    color: #1a1a1a;
    position: absolute;
    top: -7px;
    left: 0;
    right: 0;
    text-align: right;
    padding-right: 8px;
    line-height: 1;
  }
  .rbc-show-more {
    font-size: 11px;
    font-weight: 500;
    color: #1a73e8;
    background: transparent;
    padding: 2px 4px;
  }
  .rbc-time-content {
    padding-top: 48px;
    position: relative;
  }
  .rbc-time-content::before {
    content: '';
    position: absolute;
    top: 0;
    left: 60px;
    right: 0;
    height: 48px;
    border-bottom: 1px solid #dadce0;
    background: #fff;
    z-index: 1;
  }
  .rbc-time-view-week .rbc-time-content::before {
    background: repeating-linear-gradient(to right, #dadce0 0, #dadce0 1px, #fff 1px, #fff calc(100% / 7));
  }
`;

const SUGGESTED_EMAILS = [];

export default function Notes() {
  const [events, setEvents] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [eventDetailsOpen, setEventDetailsOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [deals, setDeals] = useState([]);
  const [newEvent, setNewEvent] = useState({
    title: "",
    start: "",
    end: "",
    type: "mine",
    dealId: "",
  });
  const [eventFilter, setEventFilter] = useState("mine");
  const [timeFilter, setTimeFilter] = useState("all");
  const [tab, setTab] = useState("calendar");
  const [calendarView, setCalendarView] = useState("day");
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [attendeeInput, setAttendeeInput] = useState("");
  const calendarRef = useRef(null);
  const token = localStorage.getItem("authToken");
  const [event, setEvent] = useState({
    startDate: new Date(),
    startTime: new Date(),
    endDate: new Date(),
    endTime: new Date(new Date().getTime() + 60 * 60 * 1000),
  });

  const { showAddEventModal, setShowAddEventModal } = useContext(AddEventContext);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (showAddEventModal) {
      setModalOpen(true);
      setShowAddEventModal(false);
    }
  }, [showAddEventModal, setShowAddEventModal]);

  // Auto-scroll to current time in day/week view
  useEffect(() => {
    if ((calendarView === "day" || calendarView === "week") && calendarRef.current) {
      setTimeout(() => {
        const timeGutter = document.querySelector(".rbc-time-content");
        if (timeGutter) {
          const now = new Date();
          const hours = now.getHours();
          const minutes = now.getMinutes();
          const scrollPosition = ((hours + minutes / 60) * 48) - (timeGutter.clientHeight / 2) + 24;
          timeGutter.scrollTop = Math.max(0, scrollPosition);
        }
      }, 100);
    }
  }, [calendarView, calendarDate]);

  const handleConnectCalendar = () => {
    const url = "http://localhost:5000/api/gmail/login"
    window.location.href = url;
  };

  function transformGoogleEvent(meeting) {
    let start = new Date(meeting.startTime);
    let end = new Date(meeting.endTime);

    const isAllDay =
      start.toDateString() === end.toDateString() &&
      start.getHours() === 0 &&
      start.getMinutes() === 0 &&
      end.getHours() === 0 &&
      end.getMinutes() === 0;

    return {
      id: meeting._id,
      title: meeting.summary || "Untitled Meeting",
      start: start,
      end: end,
      allDay: isAllDay,
      type:
        meeting.userEmail ===
          JSON.parse(localStorage.getItem("userData"))?.email
          ? "mine"
          : "team",
      htmlLink: meeting.meetLink || "#",
      description: meeting.description || "",
      attendees: meeting.attendees || [],
      meetingId: meeting.meetingId,
      transcript: meeting.transcript,
      botStatus: meeting.botStatus,
    };
  }

  const getUserDeals = async () => {
    try {
      const response = await apiRequest("get", "/calendar/user-deals");
      console.log("deals response", response);
      if (response && response.data) {
        setDeals(response.data);
      }
    } catch (error) {
      console.error("Error fetching deals:", error);
    }
  };

  const getCalenderEvents = async () => {
    try {
      const response = await apiRequest(
        "get",
        "/calendar/get-user-event-v1?page=1&limit=100"
      );
      console.log("responsedata", response);
      if (response && response.meetings) {
        const allEvents = response.meetings
          .map(transformGoogleEvent)
          .sort((a, b) => new Date(a.start) - new Date(b.start));
        console.log("events", allEvents);
        setEvents(allEvents);
      }
    } catch (error) {
      console.error("Error fetching calendar events:", error);
    }
  };

  const checkCalender = async () => {
    try {
      const response = await apiRequest("get", `/gmail/checkToken`);
      console.log("responsedata", response);
    } catch (error) {
      console.log("error: ", error);
    }
  };

  useEffect(() => {
    getCalenderEvents();
    getUserDeals();
    checkCalender();
  }, []);

  useEffect(() => {
    if (!event.startDate || !event.startTime) return;

    const startDateTime = new Date(
      event.startDate.getFullYear(),
      event.startDate.getMonth(),
      event.startDate.getDate(),
      event.startTime.getHours(),
      event.startTime.getMinutes()
    );

    const endDateTime = new Date(startDateTime.getTime() + 60 * 60 * 1000);

    setEvent(prev => ({
      ...prev,
      endDate: endDateTime,
      endTime: endDateTime
    }));
  }, [event.startDate, event.startTime]);

  useEffect(() => {
    if (timeFilter === "daily") setCalendarView("day");
    else if (timeFilter === "weekly") setCalendarView("week");
    else setCalendarView("month");
  }, [timeFilter]);

  function filterByTime(events, filter) {
    const now = new Date();
    if (filter === "all") return events;
    if (filter === "daily") {
      return events.filter((e) => {
        const start = new Date(e.start);
        return start.toDateString() === now.toDateString();
      });
    }
    if (filter === "weekly") {
      const startOfWeek = new Date(now);
      startOfWeek.setDate(now.getDate() - now.getDay());
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      return events.filter((e) => {
        const start = new Date(e.start);
        return start >= startOfWeek && start <= endOfWeek;
      });
    }
    if (filter === "monthly") {
      return events.filter((e) => {
        const start = new Date(e.start);
        return (
          start.getMonth() === now.getMonth() &&
          start.getFullYear() === now.getFullYear()
        );
      });
    }
    return events;
  }

  const filteredEvents = filterByTime(
    events.filter((e) =>
      eventFilter === "all" ? true : e.type === eventFilter
    ),
    timeFilter
  );

  const handleSelectSlot = ({ start, end }) => {
    setNewEvent({ title: "", start, end, type: eventFilter });
    setModalOpen(true);
  };

  const handleAddEvent = async () => {
    if (!newEvent.title) {
      alert("Please enter a meeting title");
      return;
    }

    if (!newEvent.dealId) {
      alert("Please select a deal");
      return;
    }

    if (!event.startDate || !event.startTime || !event.endDate || !event.endTime) {
      alert("Please select start and end date/time");
      return;
    }

    const startDateTime = new Date(
      event.startDate.getFullYear(),
      event.startDate.getMonth(),
      event.startDate.getDate(),
      event.startTime.getHours(),
      event.startTime.getMinutes()
    );

    const endDateTime = new Date(
      event.endDate.getFullYear(),
      event.endDate.getMonth(),
      event.endDate.getDate(),
      event.endTime.getHours(),
      event.endTime.getMinutes()
    );

    if (endDateTime <= startDateTime) {
      alert("End time must be after start time");
      return;
    }

    const userData = JSON.parse(localStorage.getItem("userData"));
    const email = userData?.email || "";
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const payload = {
      email,
      summary: newEvent.title,
      description: newEvent.description || "",
      startTime: startDateTime.toISOString(),
      endTime: endDateTime.toISOString(),
      timeZone: userTimezone,
      attendees: newEvent.attendees || [],
      conversationType: newEvent.conversationType || "meet",
      dealId: newEvent.dealId || null,
    };

    try {
      await apiRequest("post", "/calendar/create-event-v1", payload);

      setNewEvent({ title: "", description: "", attendees: [], dealId: "" });
      setEvent({
        startDate: new Date(),
        startTime: new Date(),
        endDate: new Date(),
        endTime: new Date(new Date().getTime() + 60 * 60 * 1000),
      });
      setModalOpen(false);
      getCalenderEvents();

      alert("Meeting created successfully!");
    } catch (err) {
      console.error("Failed to create event:", err);
      alert("Failed to create meeting. Please try again.");
    }
  };

  const handleSync = () => {
    getCalenderEvents();
    console.log("Sync with Google Calendar coming soon!");
  };

  const handleDeleteSelectedEvent = async () => {
    if (!selectedEvent) return;
    const userData = JSON.parse(localStorage.getItem("userData"));
    const email = userData?.email || "";
    const eventId = selectedEvent.meetingId;
    if (!email || !eventId) {
      console.error("Missing email or eventId for deletion");
      return;
    }
    try {
      await apiRequest("delete", "/calendar/delete-event-v1", { email, eventId });
      setEventDetailsOpen(false);
      setSelectedEvent(null);
      getCalenderEvents();
    } catch (err) {
      console.error("Failed to delete event:", err);
    }
  };

  return (
    <LocalizationProvider dateAdapter={ AdapterDateFns }>
      <div
        style={ {
          height: "calc(100vh - 80px)",
          background: "#fff",
          borderRadius: 8,
          padding: "4px 0 16px 0",
          display: "flex",
          flexDirection: "column",
        } }
      >
        <style>{ calendarThemeStyles }</style>
        <style>{ googleCalendarOverrides }</style>

        <div
          style={ {
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 16,
            gap: 16,
            flexShrink: 0,
          } }
        >
          <div style={ { display: "flex", alignItems: "center", gap: 8 } }>
            <Button
              variant="outlined"
              size="small"
              style={ {
                borderRadius: 4,
                fontWeight: 500,
                textTransform: "none",
                padding: "5px 16px",
                border: "1px solid #dadce0",
                color: "#3c4043",
                fontSize: 14,
              } }
              onClick={ () => setCalendarDate(new Date()) }
            >
              Today
            </Button>
            <IconButton
              size="small"
              onClick={ () =>
                setCalendarDate((prev) => {
                  const d = new Date(prev);
                  if (calendarView === "month") d.setMonth(d.getMonth() - 1);
                  else if (calendarView === "week") d.setDate(d.getDate() - 7);
                  else d.setDate(d.getDate() - 1);
                  return d;
                })
              }
              sx={ { padding: '6px' } }
            >
              <ArrowBackIos sx={ { fontSize: 16 } } />
            </IconButton>
            <IconButton
              size="small"
              onClick={ () =>
                setCalendarDate((prev) => {
                  const d = new Date(prev);
                  if (calendarView === "month") d.setMonth(d.getMonth() + 1);
                  else if (calendarView === "week") d.setDate(d.getDate() + 7);
                  else d.setDate(d.getDate() + 1);
                  return d;
                })
              }
              sx={ { padding: '6px' } }
            >
              <ArrowForwardIos sx={ { fontSize: 16 } } />
            </IconButton>
            <span style={ { fontWeight: 600, fontSize: 28, marginLeft: 8, color: "#1e293b" } }>
              { format(
                calendarDate,
                calendarView === "month" ? "MMMM yyyy" : "MMMM d, yyyy"
              ) }
            </span>
          </div>
          <div style={ { display: "flex", gap: "8px", alignItems: "center" } }>
            { tab == "calendar" ? (
              <Select
                value={ calendarView }
                onChange={ (e) => setCalendarView(e.target.value) }
                size="small"
                variant="outlined"
                displayEmpty
                sx={ {
                  borderRadius: "4px",
                  background: "#fff",
                  fontWeight: 500,
                  fontSize: 14,
                  height: 32,
                  minWidth: 90,
                  border: "1px solid #dadce0",
                  ".MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                  "&:hover": {
                    background: "#f8f9fa",
                  },
                } }
              >
                <MenuItem value="month">Month</MenuItem>
                <MenuItem value="week">Week</MenuItem>
                <MenuItem value="day">Day</MenuItem>
              </Select>
            ) : (
              <div></div>
            ) }
            {/* Calendar/Task toggle icons removed — Tasks moved to dedicated Tasks sidebar tab */}
          </div>
        </div>

        { tab === "calendar" ? (
          <div ref={ calendarRef } style={ { flex: 1, minHeight: 0 } }>
            <Calendar
              localizer={ localizer }
              events={ filteredEvents }
              startAccessor="start"
              endAccessor="end"
              style={ { height: "100%" } }
              selectable
              onSelectSlot={ handleSelectSlot }
              onSelectEvent={ (event) => {
                setSelectedEvent(event);
                setEventDetailsOpen(true);
              } }
              popup
              view={ calendarView }
              onView={ setCalendarView }
              date={ calendarDate }
              onNavigate={ setCalendarDate }
              toolbar={ false }
              components={ {
                event: CustomEvent,
                week: {
                  header: CustomTimeHeader,
                },
                day: {
                  header: CustomTimeHeader,
                },
              } }
              step={ 60 }
              timeslots={ 1 }
              dayLayoutAlgorithm="no-overlap"
            />
          </div>
        ) : (
          <div style={ { maxWidth: 600, margin: "0 auto", flex: 1 } }>
            {/* Tasks moved to dedicated Tasks page in sidebar */}
            <div style={{ textAlign: "center", padding: "48px 0", color: "#9ca3af" }}>
              <p style={{ fontSize: 15 }}><strong>Tasks</strong> tab in the sidebar.</p>
            </div>
          </div>
        ) }

        <Dialog
          open={ eventDetailsOpen }
          onClose={ () => setEventDetailsOpen(false) }
          PaperProps={ {
            style: { borderRadius: 8, maxWidth: 480, width: "100%" },
          } }
        >
          <DialogTitle sx={ { fontSize: 18, fontWeight: 500, color: "#202124" } }>{ selectedEvent?.title || "Event" }</DialogTitle>
          <DialogContent>
            <div style={ { display: "flex", flexDirection: "column", gap: 8 } }>
              <div style={ { fontSize: 14, color: "#5f6368" } }>
                { selectedEvent &&
                  `${format(new Date(selectedEvent.start), "PPpp")} - ${format(
                    new Date(selectedEvent.end),
                    "PPpp"
                  )}` }
              </div>
              { selectedEvent?.htmlLink && (
                <a
                  href={ selectedEvent.htmlLink }
                  target="_blank"
                  rel="noreferrer"
                  style={ { fontSize: 14, color: "#1a73e8" } }
                >
                  Open meeting link
                </a>
              ) }
            </div>
          </DialogContent>
          <DialogActions sx={ { padding: "8px 16px 16px" } }>
            <Button
              onClick={ () => setEventDetailsOpen(false) }
              sx={ { borderRadius: 4, textTransform: "none", color: "#5f6368" } }
            >
              Close
            </Button>
            <Button
              color="error"
              variant="outlined"
              onClick={ handleDeleteSelectedEvent }
              sx={ { borderRadius: 4, textTransform: "none" } }
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={ modalOpen }
          onClose={ () => setModalOpen(false) }
          PaperProps={ {
            style: {
              borderRadius: "8px",
              maxWidth: "680px",
              width: "100%",
              margin: "20px",
            },
          } }
        >
          <DialogTitle
            sx={ {
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 3,
              pt: 2.5,
              pb: 1.5,
            } }
          >
            <Typography variant="h6" sx={ { fontSize: "1.25rem", fontWeight: 500, color: "#202124" } }>
              Create Meeting
            </Typography>
            <IconButton
              onClick={ () => setModalOpen(false) }
              sx={ { color: "#5f6368" } }
              size="small"
            >
              ✕
            </IconButton>
          </DialogTitle>
          <DialogContent
            sx={ {
              px: 3,
              pb: 1,
            } }
          >
            <Box
              sx={ { display: "flex", flexDirection: "column", gap: 2.5, mt: 1 } }
            >
              <TextField
                placeholder="Add title"
                variant="outlined"
                value={ newEvent.title }
                onChange={ (e) =>
                  setNewEvent({ ...newEvent, title: e.target.value })
                }
                fullWidth
                sx={ {
                  "& .MuiOutlinedInput-root": {
                    fontSize: "1rem",
                    borderRadius: "4px",
                    "& fieldset": {
                      borderColor: "#dadce0",
                    },
                    "&:hover fieldset": {
                      borderColor: "#5f6368",
                    },
                  },
                } }
              />

              <FormControl fullWidth>
                <InputLabel id="deal-select-label" sx={ { fontSize: "0.875rem" } }>Select Deal</InputLabel>
                <Select
                  labelId="deal-select-label"
                  label="Select Deal"
                  value={ newEvent.dealId || "" }
                  onChange={ (e) =>
                    setNewEvent({ ...newEvent, dealId: e.target.value })
                  }
                  sx={ {
                    fontSize: "0.875rem",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#dadce0",
                    },
                  } }
                >
                  { deals.map((deal) => (
                    <MenuItem key={ deal._id } value={ deal._id }>
                      { deal.title }
                    </MenuItem>
                  )) }
                </Select>
              </FormControl>

              <Box>
                <Autocomplete
                  freeSolo
                  options={ SUGGESTED_EMAILS.filter(
                    (email) =>
                      email
                        .toLowerCase()
                        .includes(attendeeInput.toLowerCase()) &&
                      !(newEvent.attendees || []).includes(email)
                  ) }
                  inputValue={ attendeeInput }
                  onInputChange={ (_, value) => setAttendeeInput(value) }
                  onChange={ (_, value) => setAttendeeInput(value || "") }
                  renderInput={ (params) => (
                    <TextField
                      { ...params }
                      placeholder="Add attendee email"
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: false }}
                      onKeyDown={ (e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          const email = attendeeInput.trim();
                          if (
                            email &&
                            /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email) &&
                            !(newEvent.attendees || []).includes(email)
                          ) {
                            setNewEvent({
                              ...newEvent,
                              attendees: [...(newEvent.attendees || []), email],
                            });
                            setAttendeeInput("");
                          }
                        }
                      } }
                      InputProps={ {
                        ...params.InputProps,
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={ () => {
                                const email = attendeeInput.trim();
                                if (
                                  email &&
                                  /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email) &&
                                  !(newEvent.attendees || []).includes(email)
                                ) {
                                  setNewEvent({
                                    ...newEvent,
                                    attendees: [
                                      ...(newEvent.attendees || []),
                                      email,
                                    ],
                                  });
                                  setAttendeeInput("");
                                }
                              } }
                              edge="end"
                              size="small"
                              sx={ { ml: 1 } }
                            >
                              <FiPlus />
                            </IconButton>
                          </InputAdornment>
                        ),
                      } }
                      sx={ {
                        "& .MuiOutlinedInput-root": {
                          fontSize: "0.875rem",
                          "& fieldset": {
                            borderColor: "#dadce0",
                          },
                        },
                      } }
                    />
                  ) }
                />
                <Box sx={ { mt: 1, display: "flex", flexWrap: "wrap", gap: 1 } }>
                  { (newEvent.attendees || []).map((email, idx) => (
                    <Chip
                      key={ email }
                      label={ email }
                      onDelete={ () => {
                        setNewEvent({
                          ...newEvent,
                          attendees: newEvent.attendees.filter(
                            (e) => e !== email
                          ),
                        });
                      } }
                      size="small"
                      sx={ {
                        borderRadius: "16px",
                        background: "#e8f0fe",
                        color: "#1967d2",
                        fontSize: "0.8125rem",
                      } }
                    />
                  )) }
                </Box>
              </Box>

              <Box sx={ { display: "flex", gap: 1.5, mt: 1 } }>
                <DatePicker
                  label="Start Date"
                  value={ event.startDate }
                  onChange={ (newValue) => {
                    if (newValue) {
                      setEvent({ ...event, startDate: newValue });
                    }
                  } }
                  closeOnSelect={ false }
                  slotProps={ {
                    textField: {
                      size: "small",
                      fullWidth: true,
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          fontSize: "0.875rem",
                          '& fieldset': {
                            borderColor: "#dadce0",
                          },
                        },
                      },
                    },
                  } }
                />

                <TimePicker
                  label="Start Time"
                  value={ event.startTime }
                  onChange={ (newValue) => {
                    if (newValue) {
                      setEvent({ ...event, startTime: newValue });
                    }
                  } }
                  slotProps={ {
                    textField: {
                      size: "small",
                      fullWidth: true,
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          fontSize: "0.875rem",
                          '& fieldset': {
                            borderColor: "#dadce0",
                          },
                        },
                      },
                    },
                  } }
                />
              </Box>

              <Box sx={ { display: "flex", gap: 1.5 } }>
                <DatePicker
                  label="End Date"
                  value={ event.endDate }
                  onChange={ (newValue) => {
                    if (newValue) {
                      setEvent({ ...event, endDate: newValue });
                    }
                  } }
                  minDate={ event.startDate }
                  closeOnSelect={ false }
                  slotProps={ {
                    textField: {
                      size: "small",
                      fullWidth: true,
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          fontSize: "0.875rem",
                          '& fieldset': {
                            borderColor: "#dadce0",
                          },
                        },
                      },
                    },
                  } }
                />

                <TimePicker
                  label="End Time"
                  value={ event.endTime }
                  onChange={ (newValue) => {
                    if (newValue) {
                      setEvent({ ...event, endTime: newValue });
                    }
                  } }
                  slotProps={ {
                    textField: {
                      size: "small",
                      fullWidth: true,
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          fontSize: "0.875rem",
                          '& fieldset': {
                            borderColor: "#dadce0",
                          },
                        },
                      },
                    },
                  } }
                />
              </Box>

              <TextField
                label="Description"
                value={ newEvent.description || "" }
                onChange={ (e) =>
                  setNewEvent({ ...newEvent, description: e.target.value })
                }
                multiline
                rows={ 3 }
                fullWidth
                sx={ {
                  "& .MuiOutlinedInput-root": {
                    fontSize: "0.875rem",
                    '& fieldset': {
                      borderColor: "#dadce0",
                    },
                  },
                } }
              />
            </Box>
          </DialogContent>

          <DialogActions
            sx={ {
              px: 3,
              pb: 2,
              pt: 1.5,
              justifyContent: "flex-end",
            } }
          >
            <Button
              onClick={ handleAddEvent }
              variant="contained"
              disabled={
                !newEvent.title ||
                !event.startDate ||
                !event.startTime ||
                !event.endDate ||
                !event.endTime ||
                !newEvent.dealId
              }
              sx={ {
                background: "#1a73e8",
                borderRadius: "4px",
                textTransform: "none",
                fontWeight: 500,
                fontSize: "0.875rem",
                px: 3,
                py: 0.75,
                "&:hover": {
                  background: "#1557b0",
                },
                "&:disabled": {
                  background: "#f1f3f4",
                  color: "#80868b",
                },
              } }
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </LocalizationProvider>
  );
}